
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { Lock, CheckCircle, Clock, AlertCircle } from 'lucide-react';

interface AccessRequestCardProps {
  featureName: string;
  requestType: string;
  userId: string;
}

const AccessRequestCard: React.FC<AccessRequestCardProps> = ({ featureName, requestType, userId }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);

  useEffect(() => {
    // Check if user has an existing request
    const checkExistingRequest = async () => {
      try {
        const { data, error } = await supabase
          .from('access_requests')
          .select('id, status')
          .eq('user_id', userId)
          .eq('request_type', requestType)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) throw error;

        if (data && data.length > 0) {
          setRequestStatus(data[0].status);
          setRequestId(data[0].id);
        }
      } catch (error) {
        console.error('Error checking access request status:', error);
      }
    };

    if (userId) {
      checkExistingRequest();
    }
  }, [userId, requestType]);

  const handleRequestAccess = async () => {
    setIsLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('https://rrsgsfpxfwcnlttxnlom.supabase.co/functions/v1/request-access', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requestType }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit request');
      }

      if (result.requestId) {
        setRequestId(result.requestId);
        setRequestStatus('pending');
      }

      toast({
        title: 'Request Submitted',
        description: 'Your access request has been sent to the administrators.',
      });
    } catch (error: any) {
      console.error('Error requesting access:', error);
      toast({
        title: 'Request Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStatusMessage = () => {
    switch (requestStatus) {
      case 'pending':
        return (
          <div className="flex items-center text-amber-600 gap-2 mt-4">
            <Clock className="h-5 w-5" />
            <span>Your request is pending approval. We'll notify you once it's approved.</span>
          </div>
        );
      case 'approved':
        return (
          <div className="flex items-center text-green-600 gap-2 mt-4">
            <CheckCircle className="h-5 w-5" />
            <span>Your access has been approved! Please refresh the page to access the feature.</span>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex items-center text-red-600 gap-2 mt-4">
            <AlertCircle className="h-5 w-5" />
            <span>Your request was not approved. Please contact support for more information.</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-[#0EA5E9]" />
          {featureName} Access
        </CardTitle>
        <CardDescription>
          Request access to use the {featureName} feature
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-gray-600">
          This feature requires special permission from the administrators. Submit a request to get access.
        </p>
        
        {renderStatusMessage()}
        
        {!requestStatus && (
          <Button 
            onClick={handleRequestAccess} 
            disabled={isLoading || requestStatus === 'pending'} 
            className="w-full bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90"
          >
            {isLoading ? 'Submitting...' : 'Request Access'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default AccessRequestCard;
